import React, {useContext, useEffect} from 'react';
import {useDispatch} from 'react-redux';

import {useAuth0} from '@auth0/auth0-react';

import Loading from '@pb/kit-component-loading';

import {setAccessToken, setIdToken} from '../main/services/user-info-slice';

import {AuthenticationContext} from './authentication-context';

type Props = {
    children: React.ReactElement,
};

export const Auth0AuthenticationController: React.FC<Props> = ({children}) => {
    const dispatch = useDispatch();
    const {
        isLoading,
        isAuthenticated,
        loginWithRedirect,
        logout,
        getIdTokenClaims,
        getAccessTokenSilently,
    } = useAuth0();
    const {isLogoutRequested, requestLogout} = useContext(AuthenticationContext);

    useEffect(() => {
        if (isLogoutRequested) {
            try {
                window.localStorage.clear();
            } catch {
            }
            try {
                window.sessionStorage.clear();
            } catch {
            }
            logout({logoutParams: {returnTo: window.location.origin}});
            requestLogout(false);
        }
    }, [isLogoutRequested, requestLogout, logout]);

    useEffect(() => {
        if (!isLoading && isAuthenticated) {
            Promise.all([
                getIdTokenClaims(),
                getAccessTokenSilently(),
            ]).then(([claims, token]) => {
                if (claims?.__raw) {
                    dispatch(setIdToken(claims.__raw));
                }
                if (token) {
                    dispatch(setAccessToken(token));
                }
            }).catch(error => {
                // eslint-disable-next-line no-console
                console.warn('Failed to store tokens in Redux:', error);
            });
        }
    }, [isAuthenticated, isLoading, getIdTokenClaims, getAccessTokenSilently, dispatch]);

    useEffect(() => {
        if (!isLoading && !isAuthenticated) {
            loginWithRedirect();
        }
    }, [isLoading, isAuthenticated, loginWithRedirect]);

    if (isLoading) {
        return <Loading/>;
    }

    return children;
};
